import { useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';

export interface DialogProps {
  open: boolean;
  /** Called for every way out: the close control, Escape, the backdrop. */
  onClose: () => void;
  title: string;
  children: ReactNode;
}

/**
 * A modal dialog, over the browser's own `<dialog>` element.
 *
 * `showModal()` rather than a div with a high z-index, because the three things
 * that make a modal usable are the three things that are easiest to leave out
 * and hardest to notice missing: focus moves into the dialog and cannot leave
 * it, Escape closes it, and everything behind it is inert to a screen reader.
 * The platform does all three; a hand-rolled overlay does none of them until
 * somebody writes them, and nobody writes the third.
 *
 * `onClose` fires for every exit, including Escape and a programmatic close, so
 * the caller's state cannot drift out of step with what is on screen.
 */
export function Dialog({ open, onClose, title, children }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // Guarded both ways: calling `showModal()` on an open dialog throws, and
    // `close()` on a closed one fires a second `close` event.
    if (open && !node.open) node.showModal();
    if (!open && node.open) node.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      onCancel={onClose}
      className={[
        // `m-auto` is not decoration. The browser centres a modal `<dialog>`
        // with `margin: auto`, and the CSS reset sets `margin: 0` on
        // everything — so without this it opens pinned to the top-left corner.
        'bg-surface-raised text-ink border-border m-auto w-[min(34rem,calc(100vw-2rem))]',
        'rounded-[--radius-card] border p-5 backdrop:bg-black/40 sm:p-6',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-4">
        <h2 id={titleId} className="text-ink text-lg font-semibold">
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="text-ink-muted hover:text-ink -mt-1 px-2 py-1 text-xl leading-none"
        >
          ×
        </button>
      </div>
      {children}
    </dialog>
  );
}
